<template>
    <div class="ajustes-detail-container">
        <!-- Estado de carga -->
        <div v-if="isLoading" class="text-center py-5">
            <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
            <p class="mt-2 text-muted">{{ internalLabels.loading || 'Cargando...' }}</p>
        </div>

        <!-- Tabla de origen con CorporateDataTable -->
        <div
            v-if="!isLoading && ajustOrigen && ajustOrigen.length > 0"
            class="ajustes-detail-table-container"
        >
            <CorporateDataTable
                :items="ajustOrigen"
                :fields="tableFields"
                :labels="tableLabels"
                :default-per-page="defaultPerPage"
                :page-options="pageOptions"
                :narrow="false"
                :table-min-height="490"
                :table-max-height="490"
                :show-search="true"
                :allow-column-toggle="true"
                :enable-sorting="true"
                :filter-defs="tableFilterDefs"
                :toolbar-disabled="false"
                :show-back-button="showBackButton"
                :back-button-text="internalLabels.volver || 'Volver'"
                @back="goBack"
                class="ajustes-detail-table"
                :default-hidden-columns="defaultHiddenColumns"
            >
                <template #toolbar-actions>
                    <div class="detail-toolbar-left d-flex align-items-center">
                        <!-- ESPACIO VACÍO -->
                    </div>
                    <div class="detail-toolbar-right d-flex align-items-center">
                        <template v-if="!readOnly && infoOpcion === 2 && !secuenciaBloqueada">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn table-toolbar-add-btn-origin-ajuste ml-2"
                                @click="abrirContraAjuste()"
                            >
                                <font-awesome-icon :icon="['fas', 'tune']" class="mr-1" />
                                {{ internalLabels.ajustar || 'Ajustar' }}
                            </b-button>
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn table-toolbar-add-btn-origin-all ml-2"
                                @click="ajustarTodo()"
                            >
                                <font-awesome-icon :icon="['fas', 'playlist-add-check']" class="mr-1" />
                                {{ internalLabels.ajustarTodo || 'Ajustar Todo' }}
                            </b-button>
                        </template>
                    </div>
                </template>

                <template #empty-state>
                    <div class="text-center py-5">
                        <font-awesome-icon :icon="['fas', 'file-alt']" size="4x" class="text-muted mb-3" style="opacity: 0.3;" />
                        <p class="h5 text-muted">{{ internalLabels.noData || 'No hay cuentas de origen registradas' }}</p>
                    </div>
                </template>

                <template #cell(cuenta)="data">
                    <div class="text-left font-weight-bold">{{ data.value }}</div>
                </template>

                <template #cell(descripcion)="data">
                    <div class="text-left">{{ data.value }}</div>
                </template>

                <template #cell(saldo_actual)="data">
                    <div class="text-right">{{ formatCurrency(data.value) }}</div>
                </template>

                <template #cell(funcional1)="data">
                    <div class="text-right">{{ formatCurrency(data.value) }}</div>
                </template>

                <template #cell(funcional2)="data">
                    <div class="text-right">{{ formatCurrency(data.value) || '-' }}</div>
                </template>

                <template #cell(ajuste_actual)="data">
                    <div class="text-right">{{ formatCurrency(data.value) }}</div>
                </template>

                <template #cell(ajuste_funcional1)="data">
                    <div class="text-right">{{ formatCurrency(data.value) }}</div>
                </template>

                <template #cell(accionEditar)="data">
                    <b-button
                        v-if="!readOnly && infoOpcion === 2 && !secuenciaBloqueada"
                        style="background-color: #FF924C; width: 30px; height: 30px; border: none; border-radius: 4px;"
                        class="md-fab md-primary table-action-btn"
                        @click="editAjuste(data.item)"
                    >
                        <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs" />
                    </b-button>
                </template>
            </CorporateDataTable>
        </div>

        <div v-if="!isLoading && (!ajustOrigen || ajustOrigen.length === 0)" class="admin-table-block">
            <h5>{{ internalLabels.noData || 'No hay cuentas de origen registradas' }}</h5>
        </div>

        <!-- Modal de edición -->
        <b-modal v-model="showDialogEdit" :title="tituloModal" size="lg" @hidden="showDialogEdit = false">
            <b-form @submit.prevent="guardarAjuste">
                <b-row>
                    <b-col cols="2"><h6><b>{{ labels.cuenta }}</b></h6></b-col>
                    <b-col cols="2"><h6>{{ formEdit?.cuenta }}</h6></b-col>
                    <b-col cols="8"><h6>{{ formEdit?.descripcion }}</h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="2"><h6><b>{{ labels.saldo_actual }}</b></h6></b-col>
                    <b-col cols="2"></b-col>
                    <b-col cols="8"><h6>{{ formatCurrency(formEdit?.saldo_actual) }}</h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4"><h6><b>{{ labels.funcional1 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.promedio1 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.interna1 }}</b></h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.funcional1) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.promedio1) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.interna1) }}</h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4"><h6><b>{{ labels.funcional2 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.promedio2 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.interna2 }}</b></h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.funcional2) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.promedio2) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(formEdit?.interna2) }}</h6></b-col>
                </b-row>
                <b-row class="mt-3">
                    <b-col cols="3"><h6><b>{{ labels.ajuste_actual }}</b></h6></b-col>
                    <b-col cols="9">
                        <b-form-input
                            v-model="v_ajuste_actual"
                            type="number"
                            step="0.01"
                            @input="calcularAjustes"
                            :disabled="readOnly || eliminar || secuenciaBloqueada"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_funcional1 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_promedio1 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_interna1 }}</b></h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_funcional1) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_promedio1) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_interna1) }}</h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_funcional2 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_promedio2 }}</b></h6></b-col>
                    <b-col cols="4"><h6><b>{{ labels.ajuste_interna2 }}</b></h6></b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_funcional2) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_promedio2) }}</h6></b-col>
                    <b-col cols="4" class="border"><h6>{{ formatCurrency(v_ajuste_interna2) }}</h6></b-col>
                </b-row>
            </b-form>

            <template #footer>
                <b-button v-if="!eliminar" variant="secondary" @click="restaurarAjuste">
                    {{ labels.restaurar || 'Restaurar' }}
                </b-button>
                <b-button v-if="!eliminar" variant="primary" @click="guardarAjuste" :disabled="readOnly || secuenciaBloqueada">
                    {{ labels.guardar || 'Guardar' }}
                </b-button>
                <b-button v-if="eliminar" variant="danger" @click="eliminarAjuste" :disabled="readOnly || secuenciaBloqueada">
                    {{ labels.eliminar || 'Eliminar' }}
                </b-button>
                <b-button variant="secondary" @click="showDialogEdit = false">
                    {{ labels.cerrar || 'Cerrar' }}
                </b-button>
            </template>
        </b-modal>

        <!-- ============================================================
             MODAL: Ajuste de Consolidación (elegir cuenta específica)
             Restaurado del Vue 2 original
             ============================================================ -->
        <b-modal
            v-model="showContraAjuste"
            :title="labels.ajuste_consolidacion || 'Ajuste de Consolidación'"
            size="lg"
            centered
            @hidden="cerrarModalAjuste"
        >
            <div class="text-center mb-3">
                <h5>{{ labels.seleccionar_destino || 'Seleccione dónde aplicar el ajuste:' }}</h5>
            </div>

            <!-- Radios Origen / Destino -->
            <b-row class="mb-3">
                <b-col cols="6" class="text-center">
                    <b-button
                        :variant="tipoAjusteSeleccionado === 'origen' ? 'primary' : 'outline-secondary'"
                        class="w-100 py-3"
                        @click="onTipoAjusteChange('origen')"
                        :disabled="readOnly || secuenciaBloqueada"
                    >
                        <font-awesome-icon :icon="['fas', 'arrow-right']" class="mr-2" />
                        {{ nombreCompania || 'Origen' }}
                        <br>
                        <small>({{ cuentasOrigenDisponibles.length }} {{ labels.cuentas || 'cuentas' }})</small>
                    </b-button>
                </b-col>
                <b-col cols="6" class="text-center">
                    <b-button
                        :variant="tipoAjusteSeleccionado === 'destino' ? 'primary' : 'outline-secondary'"
                        class="w-100 py-3"
                        @click="onTipoAjusteChange('destino')"
                        :disabled="readOnly || secuenciaBloqueada"
                    >
                        <font-awesome-icon :icon="['fas', 'arrow-left']" class="mr-2" />
                        {{ nombreCompaniaDestino || 'Destino' }}
                        <br>
                        <small>({{ cuentasDestinoDisponibles.length }} {{ labels.cuentas || 'cuentas' }})</small>
                    </b-button>
                </b-col>
            </b-row>

            <!-- Lista de cuentas disponibles -->
            <div v-if="tipoAjusteSeleccionado && cuentasDisponibles.length > 0" class="mb-3">
                <label class="field-label mb-2">
                    <b>{{ labels.seleccione_cuenta || 'Seleccione la cuenta:' }}</b>
                </label>
                <div class="cuentas-list-minimal">
                    <div
                        v-for="cuenta in cuentasDisponibles"
                        :key="cuenta.cuenta"
                        class="cuenta-item-minimal"
                        :class="{ 'selected': cuentaSeleccionada && cuentaSeleccionada.cuenta === cuenta.cuenta }"
                        @click="seleccionarCuenta(cuenta)"
                    >
                        <span class="cuenta-code">{{ cuenta.cuenta }}</span>
                        <span class="cuenta-desc">{{ cuenta.descripcion || 'Sin descripción' }}</span>
                        <span class="cuenta-ajuste-minimal">{{ formato(cuenta.ajuste_funcional1) }}</span>
                    </div>
                </div>
            </div>
            <div v-else-if="tipoAjusteSeleccionado && cuentasDisponibles.length === 0" class="mb-3">
                <div class="alert alert-warning text-center">
                    {{ labels.no_accounts || 'No hay cuentas disponibles' }}
                </div>
            </div>
            <div v-else class="mb-3">
                <div class="alert alert-info text-center">
                    {{ labels.select_origen_destino || 'Seleccione Origen o Destino para ver las cuentas' }}
                </div>
            </div>

            <!-- Resumen de diferencia -->
            <div v-if="Math.abs(diferenciaTotal) > 0.01" class="summary-minimal mb-3">
                <div class="summary-row">
                    <span class="summary-label">{{ labels.diferencia_actual || 'Diferencia a ajustar:' }}</span>
                    <span class="summary-value">{{ formatCurrency(diferenciaTotal) }}</span>
                </div>
                <div class="summary-row" v-if="cuentaSeleccionada">
                    <span class="summary-label">{{ labels.cuenta_seleccionada || 'Cuenta seleccionada:' }}</span>
                    <span class="summary-value">{{ cuentaSeleccionada.cuenta }} - {{ cuentaSeleccionada.descripcion }}</span>
                </div>
            </div>

            <template #footer>
                <b-button
                    variant="primary"
                    @click="aplicarAjusteEnCuenta"
                    :disabled="!cuentaSeleccionada || Math.abs(diferenciaTotal) < 0.01 || readOnly || secuenciaBloqueada"
                >
                    <font-awesome-icon :icon="['fas', 'check']" class="mr-1" />
                    {{ labels.aplicar_ajuste || 'Aplicar Ajuste' }}
                </b-button>
                <b-button variant="secondary" @click="cerrarModalAjuste">
                    {{ labels.cancelar || 'Cancelar' }}
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import Balance from "@/services/BalanceNiif"
import CorporateDataTable from '../utils/CorporateDataTable.vue'

export default {
    name: "EliminaOrigen",
    components: { CorporateDataTable },
    props: {
        ajustOrigen: { type: Array, default: () => [] },
        codReporte: { type: [String, Number], default: null },
        id_compania: { type: [String, Number], default: null },
        vigencia: { type: [String, Number], default: null },
        nombreCompania: { type: String, default: '' },
        nombreCompaniaDestino: { type: String, default: '' },
        readOnly: { type: Boolean, default: false },
        secuenciaBloqueada: { type: Boolean, default: false },
        infoOpcion: { type: Number, default: 0 },
        infoSecuencia: { type: [String, Number], default: null },
        infoTipoAjuste: { type: Number, default: 0 },
        infoIdCompania: { type: [String, Number], default: null },
        infoIdCompania1: { type: [String, Number], default: null },
        infoVigencia: { type: [String, Number], default: null },
        tableLabels: { type: Object, default: () => ({}) },
        tableFilterDefs: { type: Array, default: () => [] },
        defaultPerPage: { type: Number, default: 10 },
        pageOptions: { type: Array, default: () => [10, 20, 50, 100] },
        showBackButton: { type: Boolean, default: false }
    },
    data: () => ({
        showDialogEdit: false,
        showContraAjuste: false,
        formEdit: {},
        v_ajuste_actual: 0,
        v_ajuste_funcional1: 0,
        v_ajuste_promedio1: 0,
        v_ajuste_interna1: 0,
        v_ajuste_funcional2: 0,
        v_ajuste_promedio2: 0,
        v_ajuste_interna2: 0,
        eliminar: false,
        isLoading: false,
        // ============================================
        // Estado del modal de ajuste (restaurado del Vue 2)
        // ============================================
        diferenciaTotal: 0,
        tipoAjusteSeleccionado: null,
        cuentaSeleccionada: null,
        cuentasOrigenDisponibles: [],
        cuentasDestinoDisponibles: [],
        cuentasOrigen: 0,
    }),
    computed: {
        internalLabels() {
            const es = this.$store.state._language == "es-ES"
            return {
                ...this.tableLabels,
                cuenta: es ? "Cuenta" : "Account",
                descripcion: es ? "Descripción" : "Description",
                saldo_actual: es ? "Saldo" : "Balance",
                funcional1: es ? "Funcional" : "Functional",
                funcional2: es ? "Funcional 2" : "Functional 2",
                promedio1: es ? "Promedio" : "Average",
                promedio2: es ? "Promedio 2" : "Average 2",
                interna1: es ? "Interna" : "Internal",
                interna2: es ? "Interna 2" : "Internal 2",
                ajuste_actual: es ? "Ajuste Saldo" : "Balance Adjustment",
                ajuste_funcional1: es ? "Ajuste Funcional" : "Functional Adjustment",
                ajuste_promedio1: es ? "Ajuste Promedio" : "Average Adjustment",
                ajuste_interna1: es ? "Ajuste Interna" : "Internal Adjustment",
                ajuste_funcional2: es ? "Ajuste Funcional 2" : "Functional Adjustment 2",
                ajuste_promedio2: es ? "Ajuste Promedio 2" : "Average Adjustment 2",
                ajuste_interna2: es ? "Ajuste Interna 2" : "Internal Adjustment 2",
                editar: es ? "Editar" : "Edit",
                eliminar: es ? "Eliminar" : "Delete",
                restaurar: es ? "Restaurar" : "Restore",
                guardar: es ? "Guardar" : "Save",
                cerrar: es ? "Cerrar" : "Close",
                diferencia: es ? "Diferencia" : "Difference",
                ajustar: es ? "Ajustar" : "Adjust",
                ajustarTodo: es ? "Ajustar Todo" : "Adjust All",
                ajuste_consolidacion: es ? "Ajuste de Consolidación" : "Consolidation Adjustment",
                seleccionar_destino: es ? "Seleccione dónde aplicar el ajuste:" : "Select where to apply the adjustment:",
                seleccione_cuenta: es ? "Seleccione la cuenta:" : "Select the account:",
                select_origen_destino: es ? "Seleccione Origen o Destino para ver las cuentas" : "Select Origin or Destination to view accounts",
                cuentas: es ? "cuentas" : "accounts",
                no_accounts: es ? "No hay cuentas disponibles" : "No accounts available",
                cuenta_seleccionada: es ? "Cuenta seleccionada:" : "Selected account:",
                diferencia_actual: es ? "Diferencia a ajustar:" : "Difference to adjust:",
                aplicar_ajuste: es ? "Aplicar Ajuste" : "Apply Adjustment",
                cancelar: es ? "Cancelar" : "Cancel",
                volver: es ? "Volver" : "Back",
                noData: es ? "No hay cuentas de origen registradas" : "No origin accounts registered",
                loading: es ? "Cargando..." : "Loading...",
            }
        },
        defaultHiddenColumns() {
            return ['saldo_actual', 'funcional1', 'funcional2']
        },
        labels() { return this.internalLabels },
        tableFields() {
            const fields = [
                { key: 'cuenta', label: this.internalLabels.cuenta, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '120px' },
                { key: 'descripcion', label: this.internalLabels.descripcion, sortable: true, thClass: 'text-center', tdClass: 'text-left align-middle', minWidth: '200px' },
                { key: 'saldo_actual', label: this.internalLabels.saldo_actual, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'funcional1', label: this.internalLabels.funcional1, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'funcional2', label: this.internalLabels.funcional2, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'ajuste_actual', label: this.internalLabels.ajuste_actual, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' },
                { key: 'ajuste_funcional1', label: this.internalLabels.ajuste_funcional1, sortable: true, thClass: 'text-center', tdClass: 'text-right align-middle', minWidth: '120px' }
            ]

            // La columna de acciones solo existe si el botón de editar se puede mostrar
            if (!this.readOnly && this.infoOpcion === 2 && !this.secuenciaBloqueada) {
                fields.push({ key: 'accionEditar', label: '', thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false, minWidth: '60px' })
            }

            return fields
        },
        cuentasDisponibles() {
            if (this.tipoAjusteSeleccionado === 'origen') return this.cuentasOrigenDisponibles
            if (this.tipoAjusteSeleccionado === 'destino') return this.cuentasDestinoDisponibles
            return []
        },
        tituloModal() {
            const tipo = this.tipoAjusteTexto(this.formEdit?.tipo_ajuste)
            return `${this.nombreCompania || 'Origen'} ${tipo}`
        }
    },
    watch: {
        ajustOrigen: {
            handler(nuevoValor) {
                this.calcularDiferencia()
                this.cuentasOrigenDisponibles = (nuevoValor || []).filter(
                    item => item.origen !== "C" && item.origen !== "c"
                )
            },
            immediate: true,
            deep: true
        },
        '$parent.ajusteDesti': {
            handler(nuevoValor) {
                this.cuentasDestinoDisponibles = (nuevoValor || []).filter(
                    item => item.origen !== "C" && item.origen !== "c"
                )
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        goBack() { this.$emit('back') },

        tipoAjusteTexto(tipo) {
            const es = this.$store.state._language == "es-ES"
            if (tipo == 1) return es ? 'Interes no controlante' : 'Non-controlling interest'
            if (tipo == 2) return es ? 'Eliminación en inversión' : 'Investment elimination'
            if (tipo == 3) return es ? 'Eliminación saldo recíproco' : 'Reciprocal balance elimination'
            if (tipo == 4) return es ? 'Ajuste del Combinado' : 'Combined adjustment'
            return es ? 'Tipo desconocido' : 'Unknown type'
        },

        formatCurrency(value) {
            if (value === 0 || value === "0" || value === null || value === "" || value === undefined) return ""
            return new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
        },

        formato(numero) {
            if (numero === undefined || numero === null || isNaN(numero)) return '0'
            return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(numero)
        },

        calcularDiferencia() {
            let totalOrigen = 0;
            let totalDestino = 0;

            if (this.ajustOrigen) {
                this.ajustOrigen.forEach(item => {
                    totalOrigen += parseFloat(item.ajuste_funcional1) || 0;
                });
            }

            if (this.$parent && this.$parent.ajusteDesti) {
                this.$parent.ajusteDesti.forEach(item => {
                    totalDestino += parseFloat(item.ajuste_funcional1) || 0;
                });
            }

            const diferencia = -(totalOrigen + totalDestino);
            const diferenciaRedondeada = Math.round(diferencia * 100) / 100;

            this.$emit('diferenciaCalculada', diferenciaRedondeada);
            return diferenciaRedondeada;
        },

        async verificarYActualizarValoresCero(item, tipo) {
            const camposAverificar = [
                'saldo_actual', 'funcional1', 'funcional2',
                'promedio1', 'promedio2', 'interna1', 'interna2',
                'ajuste_actual', 'ajuste_funcional1', 'ajuste_funcional2',
                'ajuste_promedio1', 'ajuste_promedio2', 'ajuste_interna1', 'ajuste_interna2'
            ];

            let hayCeros = false;
            for (const campo of camposAverificar) {
                if (item[campo] === 0 || item[campo] === "0") {
                    hayCeros = true;
                    break;
                }
            }

            if (!hayCeros) return false;

            const valoresActualizados = { ...item };
            for (const campo of camposAverificar) {
                if (valoresActualizados[campo] === 0 || valoresActualizados[campo] === "0") {
                    valoresActualizados[campo] = 1;
                }
            }

            try {
                await Balance.actualizarValoresCero(tipo, valoresActualizados);
                this.$toast.info("Se han actualizado valores de 0 a 1 para evitar divisiones por cero");
                return true;
            } catch (error) {
                this.$toast.error("Error al actualizar valores");
                console.error("Error:", error);
                return false;
            }
        },

        async obtenerItemActualizado(itemOriginal) {
            try {
                const respuesta = await Balance.obtenerItemActualizado(
                    this.infoVigencia || this.vigencia,
                    this.infoSecuencia,
                    itemOriginal.cuenta,
                    "O"
                );
                if (respuesta.data && respuesta.data.item) {
                    return respuesta.data.item;
                }
                return null;
            } catch (error) {
                console.error("Error al obtener item actualizado:", error);
                return null;
            }
        },

        async editAjuste(item) {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para edición.' : 'The validity is disabled for editing.')
                return
            }

            if (this.secuenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Esta secuencia (500+) está bloqueada para edición.' : 'This sequence (500+) is locked for editing.')
                return
            }

            this.eliminar = item.origen === "C"

            try {
                const actualizado = await this.verificarYActualizarValoresCero(item, "O");
                if (actualizado) {
                    const itemActualizado = await this.obtenerItemActualizado(item);
                    if (itemActualizado) {
                        this.formEdit = { ...itemActualizado };
                    } else {
                        this.formEdit = { ...item };
                    }
                } else {
                    this.formEdit = { ...item };
                }
            } catch (error) {
                console.error("Error al actualizar valores:", error);
                this.formEdit = { ...item };
            }

            this.v_ajuste_actual = this.formEdit.ajuste_actual || 0
            this.v_ajuste_funcional1 = this.formEdit.ajuste_funcional1 || 0
            this.v_ajuste_promedio1 = this.formEdit.ajuste_promedio1 || 0
            this.v_ajuste_interna1 = this.formEdit.ajuste_interna1 || 0
            this.v_ajuste_funcional2 = this.formEdit.ajuste_funcional2 || 0
            this.v_ajuste_promedio2 = this.formEdit.ajuste_promedio2 || 0
            this.v_ajuste_interna2 = this.formEdit.ajuste_interna2 || 0
            this.showDialogEdit = true
        },

        calcularAjustes() {
            const saldo = parseFloat(this.formEdit.saldo_actual) || 0
            const ajuste = parseFloat(this.v_ajuste_actual) || 0
            if (saldo === 0) {
                this.v_ajuste_funcional1 = ajuste
                this.v_ajuste_promedio1 = ajuste
                this.v_ajuste_interna1 = ajuste
                this.v_ajuste_funcional2 = ajuste
                this.v_ajuste_promedio2 = ajuste
                this.v_ajuste_interna2 = ajuste
                return
            }
            const porcentaje = ajuste / saldo
            this.v_ajuste_funcional1 = Math.round((this.formEdit.funcional1 || 0) * porcentaje * 100) / 100
            this.v_ajuste_promedio1 = Math.round((this.formEdit.promedio1 || 0) * porcentaje * 100) / 100
            this.v_ajuste_interna1 = Math.round((this.formEdit.interna1 || 0) * porcentaje * 100) / 100
            this.v_ajuste_funcional2 = Math.round((this.formEdit.funcional2 || 0) * porcentaje * 100) / 100
            this.v_ajuste_promedio2 = Math.round((this.formEdit.promedio2 || 0) * porcentaje * 100) / 100
            this.v_ajuste_interna2 = Math.round((this.formEdit.interna2 || 0) * porcentaje * 100) / 100
        },

        restaurarAjuste() {
            this.v_ajuste_actual = -(this.formEdit.saldo_actual || 0)
            this.calcularAjustes()
        },

        async guardarAjuste() {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para edición.' : 'The validity is disabled for editing.')
                return
            }
            if (this.secuenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Esta secuencia (500+) está bloqueada para guardar.' : 'This sequence (500+) is locked for saving.')
                return
            }
            this.showDialogEdit = false
            const data = {
                ...this.formEdit,
                ajuste_actual: this.v_ajuste_actual,
                ajuste_funcional1: this.v_ajuste_funcional1,
                ajuste_promedio1: this.v_ajuste_promedio1,
                ajuste_interna1: this.v_ajuste_interna1,
                ajuste_funcional2: this.v_ajuste_funcional2,
                ajuste_promedio2: this.v_ajuste_promedio2,
                ajuste_interna2: this.v_ajuste_interna2,
            }
            try {
                await Balance.grabeAjuste("O", data)
                this.$toast.success(this.$store.state._language == "es-ES" ? "Información Guardada" : "Information Saved")
                this.$emit('ajusteGuardado')
                this.calcularDiferencia()
            } catch (error) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al guardar" : "Error saving")
            }
        },

        async eliminarAjuste() {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para eliminar.' : 'The validity is disabled for deleting.')
                return
            }
            if (this.secuenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Esta secuencia (500+) está bloqueada para eliminar.' : 'This sequence (500+) is locked for deleting.')
                return
            }
            this.showDialogEdit = false
            try {
                await Balance.elimineAjuste("O", this.formEdit)
                this.$toast.success(this.$store.state._language == "es-ES" ? "Información Eliminada" : "Information Deleted")
                this.$emit('ajusteGuardado')
                this.$emit('cierraDetalle')
                this.calcularDiferencia()
            } catch (error) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al eliminar" : "Error deleting")
            }
        },

        // ============================================================
        // ABRIR MODAL DE CONTRA AJUSTE (lógica restaurada del Vue 2)
        // ============================================================
        abrirContraAjuste() {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para ajustar.' : 'The validity is disabled for adjusting.')
                return
            }
            if (this.secuenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Esta secuencia (500+) está bloqueada para ajustar.' : 'This sequence (500+) is locked for adjusting.')
                return
            }

            let totalOrigen = 0;
            let totalDestino = 0;

            if (this.ajustOrigen) {
                this.ajustOrigen.forEach(item => {
                    if (item.origen !== "C" && item.origen !== "c") {
                        totalOrigen += parseFloat(item.ajuste_funcional1) || 0;
                    }
                });
            }

            if (this.$parent && this.$parent.ajusteDesti) {
                this.$parent.ajusteDesti.forEach(item => {
                    if (item.origen !== "C" && item.origen !== "c") {
                        totalDestino += parseFloat(item.ajuste_funcional1) || 0;
                    }
                });
            }

            this.diferenciaTotal = -(totalOrigen + totalDestino);
            this.diferenciaTotal = Math.round(this.diferenciaTotal * 100) / 100;

            // Cargar las listas de cuentas disponibles (sin las "C")
            this.cuentasOrigenDisponibles = (this.ajustOrigen || []).filter(
                item => item.origen !== "C" && item.origen !== "c"
            );
            this.cuentasDestinoDisponibles = (
                (this.$parent && this.$parent.ajusteDesti) || []
            ).filter(item => item.origen !== "C" && item.origen !== "c");

            this.cuentasOrigen = this.cuentasOrigenDisponibles.length;

            // Resetear selección
            this.tipoAjusteSeleccionado = null;
            this.cuentaSeleccionada = null;

            if (Math.abs(this.diferenciaTotal) > 0.01) {
                this.showContraAjuste = true;
            } else {
                this.$toast.info(
                    this.$store.state._language == "es-ES"
                        ? "No hay diferencia para ajustar (la suma ya es 0)"
                        : "No difference to adjust (sum is already 0)"
                );
            }
        },

        // ============================================================
        // SELECCIÓN DE TIPO (ORIGEN / DESTINO)
        // ============================================================
        onTipoAjusteChange(tipo) {
            this.tipoAjusteSeleccionado = tipo;
            this.cuentaSeleccionada = null;
        },

        // ============================================================
        // SELECCIÓN DE CUENTA INDIVIDUAL
        // ============================================================
        seleccionarCuenta(cuenta) {
            this.cuentaSeleccionada = cuenta;
        },

        // ============================================================
        // APLICAR AJUSTE SOLO A LA CUENTA SELECCIONADA
        // (lógica restaurada del Vue 2: aplicarAjusteEnCuenta)
        // ============================================================
        async aplicarAjusteEnCuenta() {
            if (this.readOnly || this.secuenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'No se puede aplicar el ajuste.' : 'Cannot apply adjustment.')
                return
            }

            if (!this.cuentaSeleccionada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "Debe seleccionar una cuenta" : "You must select an account")
                return
            }

            if (Math.abs(this.diferenciaTotal) < 0.01) {
                this.$toast.info(this.$store.state._language == "es-ES" ? "No hay diferencia para ajustar" : "No difference to adjust")
                this.showContraAjuste = false
                return
            }

            try {
                const cuenta = this.cuentaSeleccionada;
                const ajusteActual = parseFloat(cuenta.ajuste_funcional1) || 0;
                const nuevoAjuste = Math.round((ajusteActual + this.diferenciaTotal) * 100) / 100;

                const tabla = this.tipoAjusteSeleccionado === 'origen' ? "O" : "D";

                const datosAjuste = {
                    vigencia: cuenta.vigencia || this.infoVigencia || this.vigencia,
                    secuencia: cuenta.secuencia || this.infoSecuencia,
                    id_compania: cuenta.id_compania,
                    cuenta: cuenta.cuenta,
                    origen: cuenta.origen,
                    tipo_ajuste: cuenta.tipo_ajuste || this.infoTipoAjuste,
                    ajuste_actual: nuevoAjuste,
                    ajuste_funcional1: nuevoAjuste,
                    ajuste_promedio1: nuevoAjuste,
                    ajuste_interna1: nuevoAjuste,
                    ajuste_funcional2: nuevoAjuste,
                    ajuste_promedio2: nuevoAjuste,
                    ajuste_interna2: nuevoAjuste
                };

                await Balance.grabeAjuste(tabla, datosAjuste);

                this.$toast.success(
                    this.$store.state._language == "es-ES"
                        ? `Ajuste aplicado a la cuenta ${cuenta.cuenta}: ${this.formato(ajusteActual)} → ${this.formato(nuevoAjuste)}`
                        : `Adjustment applied to account ${cuenta.cuenta}: ${this.formato(ajusteActual)} → ${this.formato(nuevoAjuste)}`
                );

                this.showContraAjuste = false;
                this.cuentaSeleccionada = null;
                this.tipoAjusteSeleccionado = null;
                this.$emit('ajusteGuardado');
                this.calcularDiferencia();

            } catch (error) {
                console.error("Error en aplicarAjusteEnCuenta:", error);
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al aplicar el ajuste" : "Error applying adjustment");
            }
        },

        cerrarModalAjuste() {
            this.showContraAjuste = false;
            this.tipoAjusteSeleccionado = null;
            this.cuentaSeleccionada = null;
        },

        // ============================================================
        // AJUSTAR TODO (se mantiene como estaba)
        // ============================================================
        async ajustarTodo() {
            if (this.readOnly) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'La vigencia está deshabilitada para ajustar.' : 'The validity is disabled for adjusting.')
                return
            }
            if (this.secuenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? 'Esta secuencia (500+) está bloqueada para ajustar.' : 'This sequence (500+) is locked for adjusting.')
                return
            }

            if (!this.ajustOrigen || this.ajustOrigen.length === 0) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "No hay registros para ajustar" : "No records to adjust")
                return
            }

            const confirmar = confirm(this.$store.state._language == "es-ES"
                ? `¿Ajustar todos los registros? (${this.ajustOrigen.length} registros)`
                : `Adjust all records? (${this.ajustOrigen.length} records)`
            )
            if (!confirmar) return

            await this.ejecutarAjusteTotal()
        },

        async ejecutarAjusteTotal() {
            let registrosAjustados = 0
            let errores = 0
            let registrosOmitidos = 0

            for (const item of this.ajustOrigen) {
                if (item.origen === "C") continue

                const ajusteActualExistente = parseFloat(item.ajuste_actual)
                const tieneAjusteManual = !isNaN(ajusteActualExistente) &&
                    (ajusteActualExistente > 1 || ajusteActualExistente < -1)

                if (tieneAjusteManual) {
                    registrosOmitidos++
                    continue
                }

                try {
                    const ajusteActual = -(item.saldo_actual || 0)
                    await Balance.grabeAjuste("O", {
                        ...item,
                        ajuste_actual: ajusteActual,
                        ajuste_funcional1: -(item.funcional1 || 0),
                        ajuste_promedio1: -(item.promedio1 || 0),
                        ajuste_interna1: -(item.interna1 || 0),
                        ajuste_funcional2: -(item.funcional2 || 0),
                        ajuste_promedio2: -(item.promedio2 || 0),
                        ajuste_interna2: -(item.interna2 || 0)
                    })
                    registrosAjustados++
                } catch (error) {
                    errores++
                    console.error("Error ajustando registro:", error)
                }
            }

            this.calcularDiferencia()

            if (errores === 0) {
                if (registrosOmitidos > 0) {
                    this.$toast.success(this.$store.state._language == "es-ES"
                        ? `Se ajustaron ${registrosAjustados} registros y se omitieron ${registrosOmitidos} con ajuste existente`
                        : `Adjusted ${registrosAjustados} records and skipped ${registrosOmitidos} with existing adjustment`
                    )
                } else {
                    this.$toast.success(this.$store.state._language == "es-ES"
                        ? `Se ajustaron ${registrosAjustados} registros correctamente`
                        : `Adjusted ${registrosAjustados} records successfully`
                    )
                }
            } else {
                this.$toast.warning(this.$store.state._language == "es-ES"
                    ? `Se ajustaron ${registrosAjustados} registros, se omitieron ${registrosOmitidos} y hubo ${errores} errores`
                    : `Adjusted ${registrosAjustados} records, skipped ${registrosOmitidos} and ${errores} errors`
                )
            }

            this.$emit('ajusteGuardado')
        }
    },
    mounted() {
        this.calcularDiferencia()
    }
}
</script>

<style src="../main.css"></style>
<style scoped>
.ajustes-detail-container { padding: 0; width: 100%; }
.ajustes-detail-table-container { width: 100%; }
.ajustes-detail-table { width: 100%; overflow-x: auto; }
.detail-toolbar-left { flex: 1; min-width: 0; }
.detail-toolbar-right { flex: 0 0 auto; display: flex; align-items: center; gap: 4px; }

.table-action-btn { border: none; border-radius: 4px; transition: all 0.2s; }
.table-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.table-toolbar-add-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
}

.table-toolbar-add-btn-origin-ajuste {
    background-color: #28a745;
    color: white;
    border: 1px solid #28a745;
}
.table-toolbar-add-btn-origin-ajuste:hover:not(:disabled) { background-color: #218838; border-color: #1e7e34; }
.table-toolbar-add-btn-origin-ajuste:disabled { opacity: 0.65; cursor: not-allowed; }

.table-toolbar-add-btn-origin-all {
    background-color: #ffc107;
    color: #212529;
    border: 1px solid #ffc107;
}
.table-toolbar-add-btn-origin-all:hover:not(:disabled) { background-color: #e0a800; border-color: #d39e00; }
.table-toolbar-add-btn-origin-all:disabled { opacity: 0.65; cursor: not-allowed; }

.admin-table-block { background: white; border-radius: 8px; padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
.admin-table-block h5 { color: #6c757d; text-align: center; margin: 0; }

/* ===== Lista de cuentas del modal de ajuste ===== */
.field-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #444;
    margin-bottom: 8px;
}

.cuentas-list-minimal {
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fafafa;
}

.cuenta-item-minimal {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 14px;
    cursor: pointer;
    transition: background 0.15s;
    border-bottom: 1px solid #f0f0f0;
    background: #fff;
    font-size: 13px;
}

.cuenta-item-minimal:last-child { border-bottom: none; }
.cuenta-item-minimal:hover { background-color: #f5f5f5; }
.cuenta-item-minimal.selected {
    background-color: #e3f0ff;
    border-left: 3px solid #1f4f83;
}

.cuenta-code {
    font-weight: 600;
    color: #222;
    min-width: 80px;
    flex-shrink: 0;
}

.cuenta-desc {
    flex: 1;
    color: #555;
    padding: 0 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cuenta-ajuste-minimal {
    font-weight: 600;
    color: #1f4f83;
    font-size: 13px;
    flex-shrink: 0;
}

.summary-minimal {
    background: #f8f8f8;
    border-radius: 4px;
    padding: 12px 16px;
    border: 1px solid #e8e8e8;
}

.summary-row {
    display: flex;
    justify-content: space-between;
    padding: 3px 0;
    font-size: 14px;
}

.summary-label { color: #666; }
.summary-value { font-weight: 600; color: #222; }

@media (max-width: 768px) {
    .ajustes-detail-container { min-height: 450px; }
    .detail-toolbar-left h5 { font-size: 0.8rem; }
    .table-toolbar-add-btn { font-size: 11px; padding: 0 10px; height: 28px; min-height: 28px; }
    .cuenta-item-minimal { flex-wrap: wrap; gap: 2px; }
}

@media (max-width: 576px) {
    .ajustes-detail-container { min-height: 400px; }
    .detail-toolbar-left { flex-wrap: wrap; gap: 4px; }
    .detail-toolbar-right { flex-wrap: wrap; gap: 4px; }
    .table-toolbar-add-btn { font-size: 10px; padding: 0 8px; height: 26px; min-height: 26px; }
}
</style>